import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Megaphone, Plus, Search, Calendar, Users, Target, TrendingUp,
  Edit, Eye, Play, Pause, CheckCircle, Clock, RefreshCw, AlertTriangle,
  DollarSign, Mail, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const STATUS_CONFIG = {
  "Rascunho": { color: "bg-gray-100 text-gray-800", icon: Edit },
  "Ativa": { color: "bg-green-100 text-green-800", icon: Play },
  "Pausada": { color: "bg-yellow-100 text-yellow-800", icon: Pause },
  "Concluída": { color: "bg-blue-100 text-blue-800", icon: CheckCircle },
  "Cancelada": { color: "bg-red-100 text-red-800", icon: AlertTriangle }
};

const TIPO_CAMPANHA_OPTIONS = [
  'Geral',
  'Gap Específico',
  'Emergência',
  'Sazonal',
  'Angariação de Fundos',
  'Recrutamento de Voluntários'
];

export default function CampanhasDoadoresPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [campanhas, setCampanhas] = useState([]);
  const [filtros, setFiltros] = useState({
    status: '',
    search: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    ativas: 0,
    concluidas: 0,
    valor_angariado: 0
  });
  
  // Modal criar/editar
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCampanha, setEditingCampanha] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo_campanha: 'Geral',
    objetivo_descricao: '',
    valor_meta: '',
    data_inicio: '',
    data_fim: '',
    urgente: false,
    assunto_email: '',
    mensagem_personalizada: ''
  });

  useEffect(() => {
    carregarCampanhas();
  }, []);

  const carregarCampanhas = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.status && filtros.status !== 'all') {
        params.append('status', filtros.status);
      }
      
      const res = await apiClient.get(`/campanhas-doadores?${params.toString()}`);
      const data = res.data.campanhas || res.data || [];
      setCampanhas(data);
      
      // Calcular stats
      const newStats = {
        total: data.length,
        ativas: data.filter(c => c.status === 'Ativa').length,
        concluidas: data.filter(c => c.status === 'Concluída').length,
        valor_angariado: data.reduce((acc, c) => acc + (c.valor_angariado || 0), 0)
      };
      setStats(newStats);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    carregarCampanhas();
  };

  const handleNovaCampanha = () => {
    setEditingCampanha(null);
    setFormData({
      nome: '',
      descricao: '',
      tipo_campanha: 'Geral',
      objetivo_descricao: '',
      valor_meta: '',
      data_inicio: new Date().toISOString().split('T')[0],
      data_fim: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      urgente: false,
      assunto_email: '',
      mensagem_personalizada: ''
    });
    setModalOpen(true);
  };

  const handleSaveCampanha = async () => {
    try {
      const payload = {
        nome: formData.nome,
        descricao: formData.descricao,
        tipo_campanha: formData.tipo_campanha,
        objetivo_descricao: formData.objetivo_descricao,
        valor_meta: formData.valor_meta ? parseFloat(formData.valor_meta) : null,
        data_inicio: formData.data_inicio,
        data_fim: formData.data_fim,
        urgente: formData.urgente,
        assunto_email: formData.assunto_email,
        mensagem_personalizada: formData.mensagem_personalizada
      };

      if (editingCampanha) {
        await apiClient.put(`/campanhas-doadores/${editingCampanha.id}`, payload);
        toast.success('Campanha atualizada com sucesso');
      } else {
        await apiClient.post('/campanhas-doadores', payload);
        toast.success('Campanha criada com sucesso');
      }
      
      setModalOpen(false);
      carregarCampanhas();
    } catch (error) {
      console.error('Erro ao salvar campanha:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar campanha');
    }
  };

  const handleVerCampanha = (campanhaId) => {
    // Navigate to campanhas detail - to be implemented
    toast.info('Detalhes da campanha em desenvolvimento');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-PT');
    } catch { return dateStr; }
  };

  const formatCurrency = (value) => {
    if (!value) return '€0';
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const filteredCampanhas = campanhas.filter(c => {
    if (filtros.search) {
      const search = filtros.search.toLowerCase();
      return c.nome?.toLowerCase().includes(search) || c.codigo_campanha?.toLowerCase().includes(search);
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="campanhas-doadores-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Megaphone className="w-8 h-8 text-purple-500" />
            Campanhas de Doadores
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Gestão de campanhas de angariação e contacto
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => navigate('/doadores')}
            variant="outline"
          >
            Voltar a Doadores
          </Button>
          <Button onClick={handleNovaCampanha} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Nova Campanha
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Campanhas</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
              </div>
              <Megaphone className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50 dark:bg-green-900/10">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 dark:text-green-400">Ativas</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-400">{stats.ativas}</p>
              </div>
              <Play className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Concluídas</p>
                <p className="text-2xl font-bold text-blue-600">{stats.concluidas}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Angariado</p>
                <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.valor_angariado)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label>Pesquisar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Nome ou código..."
                  value={filtros.search}
                  onChange={e => setFiltros(p => ({ ...p, search: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <Label>Status</Label>
              <Select 
                value={filtros.status} 
                onValueChange={v => setFiltros(p => ({ ...p, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Rascunho">Rascunho</SelectItem>
                  <SelectItem value="Ativa">Ativa</SelectItem>
                  <SelectItem value="Pausada">Pausada</SelectItem>
                  <SelectItem value="Concluída">Concluída</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch} disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Campanhas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5" />
            Campanhas ({filteredCampanhas.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campanha</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Período</TableHead>
                <TableHead className="text-center">Doadores</TableHead>
                <TableHead className="text-center">Meta</TableHead>
                <TableHead className="text-center">Angariado</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCampanhas.map((campanha) => {
                const StatusIcon = STATUS_CONFIG[campanha.status]?.icon || Clock;
                const progresso = campanha.valor_meta 
                  ? Math.min(100, (campanha.valor_angariado / campanha.valor_meta) * 100)
                  : 0;
                
                return (
                  <TableRow key={campanha.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{campanha.nome}</p>
                        <p className="text-xs text-slate-500 font-mono">{campanha.codigo_campanha}</p>
                      </div>
                      {campanha.urgente && (
                        <Badge className="bg-red-500 text-white text-xs mt-1">URGENTE</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{campanha.tipo_campanha}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(campanha.data_inicio)}
                        </p>
                        <p className="text-slate-500">até {formatDate(campanha.data_fim)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Users className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">{campanha.num_doadores_contactados || 0}</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {campanha.num_respostas_positivas || 0} positivas
                      </p>
                    </TableCell>
                    <TableCell className="text-center">
                      {campanha.valor_meta ? (
                        <p className="font-medium">{formatCurrency(campanha.valor_meta)}</p>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <p className="font-medium text-purple-600">
                        {formatCurrency(campanha.valor_angariado)}
                      </p>
                      {campanha.valor_meta > 0 && (
                        <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1">
                          <div 
                            className="bg-purple-600 h-1.5 rounded-full" 
                            style={{ width: `${progresso}%` }}
                          />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_CONFIG[campanha.status]?.color}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {campanha.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleVerCampanha(campanha.id)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          {filteredCampanhas.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-500">
              <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Nenhuma campanha encontrada</p>
              <Button onClick={handleNovaCampanha} variant="link" className="mt-2">
                Criar primeira campanha
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar/Editar Campanha */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCampanha ? 'Editar Campanha' : 'Nova Campanha'}
            </DialogTitle>
            <DialogDescription>
              {editingCampanha ? 'Atualize os dados da campanha' : 'Crie uma nova campanha de contacto a doadores'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome da Campanha *</Label>
                <Input
                  value={formData.nome}
                  onChange={e => setFormData(p => ({ ...p, nome: e.target.value }))}
                  placeholder="Ex: Campanha de Inverno 2026"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Campanha</Label>
                <Select 
                  value={formData.tipo_campanha} 
                  onValueChange={v => setFormData(p => ({ ...p, tipo_campanha: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPO_CAMPANHA_OPTIONS.map(tipo => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Meta de Angariação (€)</Label>
                <Input
                  type="number"
                  value={formData.valor_meta}
                  onChange={e => setFormData(p => ({ ...p, valor_meta: e.target.value }))}
                  placeholder="Ex: 5000"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data de Início *</Label>
                <Input
                  type="date"
                  value={formData.data_inicio}
                  onChange={e => setFormData(p => ({ ...p, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Data de Fim *</Label>
                <Input
                  type="date"
                  value={formData.data_fim}
                  onChange={e => setFormData(p => ({ ...p, data_fim: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.descricao}
                onChange={e => setFormData(p => ({ ...p, descricao: e.target.value }))}
                placeholder="Descreva o objetivo e contexto da campanha..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Objetivo Específico</Label>
              <Textarea
                value={formData.objetivo_descricao}
                onChange={e => setFormData(p => ({ ...p, objetivo_descricao: e.target.value }))}
                placeholder="Ex: Angariar 500 cobertores para o inverno..."
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="urgente"
                checked={formData.urgente}
                onCheckedChange={v => setFormData(p => ({ ...p, urgente: v }))}
              />
              <Label htmlFor="urgente" className="cursor-pointer font-normal">
                Marcar como URGENTE (prioridade alta)
              </Label>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Comunicação
              </h4>
              <div className="space-y-4">
                <div>
                  <Label>Assunto do Email</Label>
                  <Input
                    value={formData.assunto_email}
                    onChange={e => setFormData(p => ({ ...p, assunto_email: e.target.value }))}
                    placeholder="Ex: Ajude-nos nesta campanha de inverno"
                  />
                </div>
                <div>
                  <Label>Mensagem Personalizada</Label>
                  <Textarea
                    value={formData.mensagem_personalizada}
                    onChange={e => setFormData(p => ({ ...p, mensagem_personalizada: e.target.value }))}
                    placeholder="Mensagem adicional para incluir nos emails..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveCampanha}
              disabled={!formData.nome || !formData.data_inicio || !formData.data_fim}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {editingCampanha ? 'Atualizar' : 'Criar'} Campanha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
